import { createBrowserRouter, redirect } from "react-router";
import { useAuthStore } from "../stores/authStore";
import { AuthGuard } from "./components/AuthGuard";
import { MainLayout } from "./components/layout/MainLayout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Catalogs } from "./pages/Catalogs";
import { Profiles } from "./pages/Profiles";
import { Mappings } from "./pages/Mappings";
import { ComponentLibrary } from "./pages/ComponentLibrary";
import { SSPEditor } from "./pages/SSPEditor";
import { AssessmentPlans } from "./pages/AssessmentPlans";
import { AssessmentPlanDetail } from "./pages/AssessmentPlanDetail";
import { AssessmentPlanWizard } from "./pages/AssessmentPlanWizard";
import { AssessmentResults } from "./pages/AssessmentResults";
import { AssessmentResultDetail } from "./pages/AssessmentResultDetail";
import { AssessmentExecutionList } from "./pages/AssessmentExecutionList";
import { AssessmentExecution } from "./pages/AssessmentExecution";
import { AssessmentReviewList } from "./pages/AssessmentReviewList";
import { AssessmentReview } from "./pages/AssessmentReview";
import { POAM } from "./pages/POAM";
import { POAMDetail } from "./pages/POAMDetail";
import { POAMItemDetail } from "./pages/POAMItemDetail";
import { Telemetry } from "./pages/Telemetry";
import { Governance } from "./pages/Governance";
import { AuditLogs } from "./pages/AuditLogs";
import { Users } from "./pages/Users";
import { Settings } from "./pages/Settings";
import { LicenseManagement } from "./pages/LicenseManagement";
import {
  ContentRegistry,
  ProcessingConsolePage,
  ProcessingConsole3DPage,
} from "./pages/ContentRegistry";
import { ComponentDefinitionVisualizer } from "./pages/visualizer/ComponentDefinitionVisualizer";
import { DirectoryConfiguration } from "./pages/DirectoryConfiguration";
import { OrganizationManagement } from "./pages/OrganizationManagement";
import { RoleManagement } from "./pages/RoleManagement";
import { UserManagement } from "./pages/UserManagement";
import { CISODashboard } from "./pages/CISODashboard";
// import { GRCDashboard } from "./pages/GRCDashboard"; // replaced by GRCDashboardNew
// import { GRCDashboardNew } from "./pages/GRCDashboardNew"; // now the per-org drill-down inside GRCManagerDashboard
import { GRCManagerDashboard } from "./pages/GRCManagerDashboard/GRCManagerDashboard";
import { GroupAuditorDashboard } from "./pages/GroupAuditorDashboard/GroupAuditorDashboard";
import { SystemOwnerDashboard } from "./pages/SystemOwnerDashboard/SystemOwnerDashboard";
import { DashboardRouter } from "./pages/DashboardRouter";
import { QuadrantDashboard } from "./pages/QuadrantDashboard";
import { ExecutiveInsights } from "./pages/ExecutiveInsights";
import { ReportsList } from "./pages/ReportsList";
import { ReportDetails } from "./pages/ReportDetails";
import { CMDBApplications } from "./pages/CMDBApplications";
import { CMDBApplicationDetail } from "./pages/CMDBApplicationDetail";
import { CMDBApplicationAssetDetail } from "./pages/CMDBApplicationAssetDetail";
import { CMDBAssets } from "./pages/CMDBAssets";
import { CMDBConfigItems } from "./pages/CMDBConfigItems";
import { CMDBServices } from "./pages/CMDBServices";
import { CMDBServiceDetail } from "./pages/CMDBServiceDetail";
import { CMDBCloudAccounts } from "./pages/CMDBCloudAccounts";
import { CMDBCloudAccountDetail } from "./pages/CMDBCloudAccountDetail";
import { CMDBDeviceDetail } from "./pages/CMDBDeviceDetail";
import { LlmConfigurations } from "./pages/LlmConfigurations";
import { AiInteractionController } from "./pages/AiInteractionController";
import { ValidationRules } from "./pages/ValidationRules";
import { DashboardConfig } from "./pages/DashboardConfig/DashboardConfig";
import { DriftAnalysisWorkspace } from "./pages/grc/DriftAnalysisWorkspace";
import { ArchitectureCompliance } from "./pages/ArchitectureCompliance";
import { AlertCalendar } from "./pages/AlertCalendar/AlertCalendar";
import { Schedulers } from "./pages/Schedulers";
import { SchedulerConfiguration } from "./pages/SchedulerConfiguration";
import { ChangeAssuranceGovernancePage } from "./pages/ChangeAssuranceGovernance/ChangeAssuranceGovernancePage";
import { LogReviewPage } from "./pages/LogReview/LogReviewPage";
import { LogReviewerInsightsPage } from "./pages/LogReviewerInsights/LogReviewerInsightsPage";
import { CisoLogReviewPage } from "./pages/CisoLogReview/CisoLogReviewPage";
import { PrivilegeAccessGovernance } from "./pages/PrivilegeAccessGovernance/PrivilegeAccessGovernance";
import { IamPolicyCategorization } from "./pages/PrivilegeAccessGovernance/IamPolicyCategorization";

export const router = createBrowserRouter([
  {
    path: "/login",
    Component: Login,
  },
  {
    path: "/",
    Component: AuthGuard,
    children: [
      {
        Component: MainLayout,
        children: [
          { index: true, loader: () => redirect("/dashboard/executive-insights") },
          {  path: "dashboard", Component: DashboardRouter },
      { path: "dashboard/platform-owner", Component: Dashboard },
          { path: "dashboard/security-posture-dashboard", Component: CISODashboard },
          { path: "dashboard/grc-manager", Component: GRCManagerDashboard },
          { path: "dashboard/group-auditor", Component: GroupAuditorDashboard },
          { path: "dashboard/system-owner", Component: SystemOwnerDashboard },
          { path: "dashboard/quadrant", Component: QuadrantDashboard },
          { path: "dashboard/executive-insights", Component: ExecutiveInsights },
          { path: "continuous-compliance/governance/catalog", Component: Catalogs },
          { path: "continuous-compliance/governance/catalog/:id", Component: Catalogs },
          { path: "continuous-compliance/governance/profile", Component: Profiles },
          { path: "continuous-compliance/governance/profile/:id", Component: Profiles },
          { path: "continuous-compliance/governance/control-mapping", Component: Mappings },
          { path: "continuous-compliance/governance/control-mapping/:id", Component: Mappings },
          { path: "continuous-compliance/system/component-definition", Component: ComponentLibrary },
          { path: "continuous-compliance/system/component-definition/:id", Component: ComponentLibrary },
          { path: "continuous-compliance/system/system-security-plan", Component: SSPEditor },
          { path: "continuous-compliance/system/system-security-plan/:id", Component: SSPEditor },
          { path: "continuous-compliance/assessment/assessment-plan", Component: AssessmentPlans },
          { path: "continuous-compliance/assessment/assessment-plan/wizard", Component: AssessmentPlanWizard },
          { path: "continuous-compliance/assessment/assessment-plan/:id", Component: AssessmentPlanDetail },
          { path: "continuous-compliance/assessment/assessment-execution", Component: AssessmentExecutionList },
          { path: "continuous-compliance/assessment/assessment-execution/:id", Component: AssessmentExecution },
          { path: "continuous-compliance/assessment/assessment-review", Component: AssessmentReviewList },
          { path: "continuous-compliance/assessment/assessment-review/:id", Component: AssessmentReview },
          { path: "continuous-compliance/assessment/assessment-result", Component: AssessmentResults },
          { path: "continuous-compliance/assessment/assessment-result/:id", Component: AssessmentResultDetail },
          { path: "continuous-compliance/assessment/drift-management", Component: DriftAnalysisWorkspace },
          { path: "continuous-compliance/risk/poam", Component: POAM },
          { path: "continuous-compliance/risk/poam/:id", Component: POAMDetail },
          { path: "continuous-compliance/risk/poam/:id/item/:itemId", Component: POAMItemDetail },
          { path: "privilege-access-governance", Component: PrivilegeAccessGovernance },
          { path: "report", Component: ReportsList },
          { path: "report/:sspId", Component: ReportDetails },
          { path: "telemetry", Component: Telemetry },
          { path: "continuous-compliance/governance/content-registry", Component: ContentRegistry },
          { path: "continuous-compliance/governance/content-registry/console", Component: ProcessingConsolePage },
          { path: "continuous-compliance/governance/content-registry/console-3d", Component: ProcessingConsole3DPage },
          { path: "continuous-compliance/governance/content-registry/:id", Component: ContentRegistry },
          { path: "governance", Component: Governance },
          { path: "administration/audit-log", Component: AuditLogs },
          { path: "administration/schedulers", Component: Schedulers },
          { path: "administration/schedulers/:id/configuration", Component: SchedulerConfiguration },
          { path: "user-management", Component: Users },
          { path: "setting", Component: Settings },
          { path: "administration/license-management", Component: LicenseManagement },
          { path: "component-definition-visualizer", Component: ComponentDefinitionVisualizer },
          { path: "cmdb/applications", Component: CMDBApplications },
          { path: "cmdb/applications/:id", Component: CMDBApplicationDetail },
          {
            path: "cmdb/applications/:applicationId/assets/:assetId",
            Component: CMDBApplicationAssetDetail,
          },
          { path: "cmdb/assets", Component: CMDBAssets },
          { path: "cmdb/devices", Component: CMDBConfigItems },
          { path: "cmdb/devices/:id", Component: CMDBDeviceDetail },
          { path: "cmdb/services", Component: CMDBServices },
          { path: "cmdb/services/:id", Component: CMDBServiceDetail },
          { path: "cmdb/cloud-accounts", Component: CMDBCloudAccounts },
          {
            path: "cmdb/cloud-accounts/:id",
            Component: CMDBCloudAccountDetail,
          },
          { path: "administration/directory-configuration", Component: DirectoryConfiguration },
          { path: "administration/organization", Component: OrganizationManagement },
          { path: "administration/roles", Component: RoleManagement },
          { path: "administration/users", Component: UserManagement },
          { path: "administration/llm-configuration", Component: LlmConfigurations },
          {
            path: "administration/ai-module-configuration",
            Component: AiInteractionController,
          },
          { path: "administration/validation-rules", Component: ValidationRules },
          { path: "administration/alert-calendar", Component: AlertCalendar },
          {
            path: "administration/iam-policy-categorization",
            Component: IamPolicyCategorization,
          },

                { path: "administration/insights-configuration", Component: DashboardConfig },
                { path: "architecture-compliance", Component: ArchitectureCompliance },

          // ── Change Assurance Governance ─────────────────────────────────
          // One destination with three routed tabs behind it. Nested rather
          // than three sibling paths: `/change-assurance-governance-ciso`
          // shares a prefix with the base, which is what made every sibling
          // light up together in the sidebar.
          //
          // TODO(rbac): gate this subtree on change-assurance-governance once
          // the Log Review modules land in RBAC_MATRIX.
          {
            path: "change-assurance-governance",
            Component: ChangeAssuranceGovernancePage,
            children: [
              { index: true, Component: LogReviewPage },
              { path: "reviewer-insights", Component: LogReviewerInsightsPage },
              { path: "ciso-insights", Component: CisoLogReviewPage },
            ],
          },
        ],
      },
    ],
  },
  {
    // An unmatched URL must never silently bounce an authenticated user back to
    // the login form: that is what turned a typo'd redirect target into an
    // invisible "login does nothing" bug — no error, no toast, just the form
    // again. Only visitors without a session belong on /login. Authenticated
    // users go back to the last page that actually rendered (tracked in
    // MainLayout), falling back to the default dashboard if none is recorded.
    path: "*",
    loader: () => {
      if (!useAuthStore.getState().getToken()) return redirect("/login");
      const lastVisited = sessionStorage.getItem("lastVisitedPath");
      return redirect(lastVisited || "/dashboard/executive-insights");
    },
  },
]);
